import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { format } from "date-fns";
import { Sparkles, Flame } from "lucide-react";

const JAR_IMG = "https://static.prod-images.emergentagent.com/jobs/501ff75c-3ac8-43b3-9bce-7aac7ef89b3b/images/411529246eb7dcfcefb5c69da7c7c2cab1d7d1c1ba67a1fec22ef41c089cc1c4.png";

export default function Gratitude() {
  const [content, setContent] = useState("");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ total: 0, streak: 0, days: [] });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([api.get("/gratitude"), api.get("/gratitude/stats")]);
      setItems(a.data);
      setStats(b.data);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true);
    try {
      await api.post("/gratitude", { content });
      setContent("");
      toast.success("Dropped in the jar.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save.");
    } finally {
      setBusy(false);
    }
  };

  const filledDays = new Set(stats.days || []);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 grid grid-cols-1 lg:grid-cols-12 gap-12" data-testid="page-gratitude">
      <div className="lg:col-span-7">
        <div className="text-xs tracking-[0.25em] uppercase text-mindora-red mb-4">
          <span className="editorial-rule" />The gratitude jar
        </div>
        <h1 className="font-serif text-5xl text-mindora-ink leading-tight">
          Small things,<br /><em className="italic text-mindora-red">collected slowly.</em>
        </h1>
        <p className="mt-4 text-mindora-muted leading-relaxed max-w-lg">
          A warm meal. A funny conversation. The walk you nearly skipped. Drop in one note a day —
          quietly, over weeks, perspective shifts.
        </p>

        <form onSubmit={add} className="mt-10" data-testid="gratitude-form">
          <Textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={3}
            maxLength={500}
            data-testid="gratitude-input"
            placeholder="Today I was glad about…"
            className="bg-mindora-bg border-mindora-line focus-visible:ring-mindora-red font-serif text-lg leading-relaxed"
          />
          <div className="flex items-center justify-between mt-3">
            <div className="text-xs text-mindora-muted">{content.length}/500</div>
            <Button type="submit" disabled={busy} data-testid="gratitude-submit-button" className="btn-primary rounded-full px-6 py-2 h-auto text-sm">
              {busy ? "Adding…" : "Add to jar"}
            </Button>
          </div>
        </form>

        <div className="mt-10 grid grid-cols-2 gap-4">
          <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-5">
            <div className="flex items-center gap-2 text-mindora-red">
              <Flame size={16} />
              <span className="text-xs uppercase tracking-[0.2em]">Streak</span>
            </div>
            <div className="font-serif text-4xl mt-2 text-mindora-ink" data-testid="gratitude-streak-value">{stats.streak} <span className="text-base text-mindora-muted">days</span></div>
          </div>
          <div className="rounded-2xl border border-mindora-line bg-mindora-panel p-5">
            <div className="flex items-center gap-2 text-mindora-red">
              <Sparkles size={16} />
              <span className="text-xs uppercase tracking-[0.2em]">Total</span>
            </div>
            <div className="font-serif text-4xl mt-2 text-mindora-ink" data-testid="gratitude-total-value">{stats.total} <span className="text-base text-mindora-muted">notes</span></div>
          </div>
        </div>

        <h3 className="font-serif text-2xl text-mindora-ink mt-12">Your jar</h3>
        <div className="mt-5 space-y-3" data-testid="gratitude-list">
          {items.length === 0 && <p className="text-sm text-mindora-muted italic">Your jar is empty. Drop in your first note above.</p>}
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-mindora-line bg-mindora-bg px-5 py-4" data-testid={`gratitude-item-${it.id}`}>
              <div className="text-mindora-ink font-serif text-lg leading-snug">{it.content}</div>
              <div className="text-xs text-mindora-muted mt-1">{format(new Date(it.created_at), "PPP")}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="lg:col-span-5">
        <div className="rounded-3xl overflow-hidden border border-mindora-line shadow-clay">
          <img src={JAR_IMG} alt="" className="w-full h-72 object-cover" />
        </div>

        <h3 className="font-serif text-2xl text-mindora-ink mt-10">Your year so far</h3>
        <p className="text-xs text-mindora-muted mt-1">Days you dropped something in the jar are highlighted.</p>
        <div className="mt-4 rounded-2xl border border-mindora-line bg-mindora-panel p-3">
          <Calendar
            mode="multiple"
            selected={Array.from(filledDays).map((d) => new Date(d))}
            modifiersClassNames={{
              selected: "bg-mindora-red text-mindora-bg",
            }}
            data-testid="gratitude-calendar"
          />
        </div>
      </div>
    </div>
  );
}
